// Shared StepId ↔ pe_lesson_phase mapping (Delivery Loop).
//
// SINGLE source of truth for the engine-step ↔ DB-phase binding. BOTH the
// native and LTI surfaces import this — it must never be re-declared in a
// surface (Phase-2 guard 1). The engine (sequenceEngine.ts) stays pure and
// knows nothing about the DB; this thin map is the bridge.
//
// Approved mapping (connect/observe/practice/show, order 1–4):
//   vocab → connect · video → observe · reader → practice · activity → show
// `summative` maps to a future `assess` enum value, deferred until a T3/T4
// chapter ships (no enum change for the T1 pilot).

import type { StepId } from "./sequenceEngine";

/** The `pe_lesson_phase` enum values that exist today. */
export type LessonPhase = "connect" | "observe" | "practice" | "show";

/** Engine step → DB phase. `summative` is null until the `assess` value lands. */
export const STEP_TO_PHASE: Readonly<Record<StepId, LessonPhase | null>> = {
  vocab: "connect",
  video: "observe",
  reader: "practice",
  // formative has no `pe_lesson_phase` enum value yet, so cross-reload STEP
  // completion is not persisted (persistStepComplete degrades to a no-op — same
  // as summative). The student-facing work IS persisted per-item via /pe/submit
  // (student_assignments). Promoting this to a real phase needs an additive enum
  // value (`assess`-class) + per-chapter pe_chapter_phases rows — deferred.
  formative: null,
  activity: "show",
  summative: null, // → future `assess`; deferred (T3/T4 only)
};

/** DB phase → engine step. Inverse of the non-null entries above. */
export const PHASE_TO_STEP: Readonly<Record<LessonPhase, StepId>> = {
  connect: "vocab",
  observe: "video",
  practice: "reader",
  show: "activity",
};

/** True for a phase string the current enum/mapping recognizes. */
export function isKnownPhase(phase: string): phase is LessonPhase {
  return phase === "connect" || phase === "observe" || phase === "practice" || phase === "show";
}
